/**
 * "Розмірні ворота" — поведінка як у Zara.
 *
 * Кнопка Add to cart стоїть НАД вибором розміру, тому покупець може її
 * натиснути, не обравши розмір. Dawn у такому разі мовчки додасть варіант
 * за замовчуванням (перший доступний). Щоб цього не сталося, ми:
 *
 *   1. перехоплюємо submit форми у фазі захоплення (до <product-form>);
 *   2. якщо розмір ще не обрано явно — показуємо шторку вибору;
 *   3. після вибору чекаємо, поки Dawn оновить прихований input варіанта,
 *      і лише тоді реально сабмітимо.
 *
 * Ворота знімаються, щойно покупець обрав розмір — хоч у шторці,
 * хоч у звичайному блоці вибору нижче.
 */
class SizeGate extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.chosen = false;

    // ?variant=... в URL означає, що варіант уже заданий явно
    if (new URLSearchParams(location.search).has('variant')) this.chosen = true;

    this.pending = null;

    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.reposition = this.reposition.bind(this);

    document.addEventListener('submit', this.onSubmit, true);
    document.addEventListener('change', this.onChange, true);
    document.addEventListener('keydown', this.onKey);
    // Shop Pay і "More payment options" — власні web-компоненти Shopify.
    // Вони НЕ сабмітять форму, а ведуть на checkout напряму, тож submit
    // їх не ловить — доводиться перехоплювати клік.
    document.addEventListener('click', this.onClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onSubmit, true);
    document.removeEventListener('change', this.onChange, true);
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('keydown', this.onKey);
    removeEventListener('resize', this.reposition);
    removeEventListener('scroll', this.reposition, true);
  }

  onKey(event) {
    if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
  }

  get form() {
    return document.getElementById(this.formId);
  }

  /** Fieldset опції розміру — шукаємо за текстом legend. */
  get sizeFieldset() {
    const fieldsets = document.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input');
    for (const fs of fieldsets) {
      const legend = fs.querySelector('legend, label.form__label');
      if (!legend) continue;
      // у legend, крім назви опції, може лежати посилання на таблицю розмірів
      // та поточне значення — прибираємо їх перед порівнянням
      const clone = legend.cloneNode(true);
      clone.querySelectorAll('.sg-link, .sg-trigger, [data-selected-value], .visually-hidden').forEach((n) => n.remove());
      const name = clone.textContent.split(':')[0].trim().toLowerCase();
      if (name === 'size' || name === 'розмір') return fs;
    }
    return null;
  }

  /** Варіанти розміру як список. Порожній, якщо це не радіо-кнопки. */
  get sizeOptions() {
    const fs = this.sizeFieldset;
    if (!fs) return [];
    return [...fs.querySelectorAll('input[type="radio"]')].map((input) => {
      const label = fs.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      let text = input.value;
      if (label) {
        const clone = label.cloneNode(true);
        clone.querySelectorAll('.label-unavailable, .visually-hidden').forEach((n) => n.remove());
        text = clone.textContent.trim() || input.value;
      }
      return { input, text, soldOut: input.classList.contains('disabled'), checked: input.checked };
    });
  }

  onChange(event) {
    const fs = this.sizeFieldset;
    if (fs && fs.contains(event.target)) this.chosen = true;
  }

  onSubmit(event) {
    const form = this.form;
    if (!form || event.target !== form) return;
    // event.submitter — саме та кнопка, якою сабмітили (основна чи липка);
    // потрібна, щоб на десктопі відкрити шторку поруч із нею
    this.gate(event, { type: 'form', trigger: event.submitter });
  }

  onClick(event) {
    if (!event.target.closest) return;
    const container = event.target.closest('.shopify-payment-button');
    if (!container) return;

    const form = this.form;
    if (!form || !form.contains(container)) return;

    const which = event.target.closest('.shopify-payment-button__more-options') ? 'more' : 'wallet';
    this.gate(event, { type: 'payment', which, trigger: container });
  }

  /** Спільна перевірка для обох шляхів: пропустити або відкрити шторку. */
  gate(event, pending) {
    if (this.chosen) return;

    const options = this.sizeOptions;
    // немає розмірів або це не радіо — не заважаємо
    if (options.length < 2) {
      this.chosen = true;
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.pending = pending;
    this.open(options);
  }

  open(options) {
    clearTimeout(this.clearTimer);
    this.render(options);
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';

    this.reposition();
    addEventListener('resize', this.reposition);
    addEventListener('scroll', this.reposition, true);

    const first = this.querySelector('.size-gate__option:not([disabled])');
    if (first) first.focus({ preventScroll: true });
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    removeEventListener('resize', this.reposition);
    removeEventListener('scroll', this.reposition, true);

    // повертаємо фокус на кнопку, з якої прийшли
    const trigger = this.pending && this.pending.trigger;
    if (trigger && typeof trigger.focus === 'function') trigger.focus({ preventScroll: true });

    // Підстраховка до CSS: після анімації прибираємо вузли зовсім.
    // Прозорий елемент і далі ловить кліки, а це ламає всю сторінку.
    clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => {
      if (!this.hasAttribute('open')) this.innerHTML = '';
    }, 300);
  }

  /**
   * На мобільних шторка лишається знизу — туди зручно дотягнутись великим
   * пальцем. На десктопі відкриваємо її впритул до натиснутої кнопки,
   * інакше вона з'їжджає в низ великого екрана, далеко від дії.
   */
  reposition() {
    const sheet = this.querySelector('.size-gate__sheet');
    if (!sheet) return;

    if (matchMedia('(max-width: 749px)').matches) {
      sheet.style.top = '';
      sheet.style.left = '';
      sheet.style.width = '';
      return;
    }

    const trigger = this.pending && this.pending.trigger;
    if (!trigger || typeof trigger.getBoundingClientRect !== 'function') return;

    const r = trigger.getBoundingClientRect();
    const gap = 8;
    const edge = 16;
    const h = sheet.offsetHeight;
    const w = Math.min(Math.max(r.width, 280), innerWidth - edge * 2);

    // під кнопкою, а якщо не влазить — над нею
    let top = r.bottom + gap;
    if (top + h > innerHeight - edge) {
      const above = r.top - gap - h;
      top = above >= edge ? above : Math.max(edge, innerHeight - edge - h);
    }

    const left = Math.max(edge, Math.min(r.left, innerWidth - edge - w));

    sheet.style.width = `${w}px`;
    sheet.style.top = `${top}px`;
    sheet.style.left = `${left}px`;
  }

  render(options) {
    this.innerHTML = '';

    const overlay = document.createElement('div');
    overlay.className = 'size-gate__overlay';
    overlay.addEventListener('click', () => this.close());

    const sheet = document.createElement('div');
    sheet.className = 'size-gate__sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', this.dataset.title || 'Select size');

    const head = document.createElement('div');
    head.className = 'size-gate__head';
    const title = document.createElement('h2');
    title.className = 'size-gate__title';
    title.textContent = this.dataset.title || 'Select size';
    head.append(title);

    // переносимо посилання на таблицю розмірів, якщо воно є в темі
    const guide = document.querySelector('.sg-link, .sg-trigger');
    if (guide) {
      const link = document.createElement('button');
      link.type = 'button';
      link.className = 'size-gate__guide';
      link.textContent = guide.textContent.trim() || 'Size guide';
      link.addEventListener('click', () => {
        this.close();
        guide.click();
      });
      head.append(link);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'size-gate__close';
    close.setAttribute('aria-label', this.dataset.close || 'Close');
    // SVG, а не символ "×" — не залежить ні від кодування файлу,
    // ні від того, чи є такий гліф у шрифті теми
    close.innerHTML =
      '<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">' +
      '<path d="M2 2 L14 14 M14 2 L2 14" stroke="currentColor" stroke-width="1.6"' +
      ' fill="none" stroke-linecap="round"/></svg>';
    close.addEventListener('click', () => this.close());
    head.append(close);

    const list = document.createElement('ul');
    list.className = 'size-gate__list';

    for (const opt of options) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'size-gate__option';
      btn.disabled = opt.soldOut;

      const value = document.createElement('span');
      value.className = 'size-gate__value';
      value.textContent = opt.text;
      btn.append(value);

      if (opt.soldOut) {
        const note = document.createElement('span');
        note.className = 'size-gate__note';
        note.textContent = this.dataset.soldOut || 'Sold out';
        btn.append(note);
      } else {
        btn.addEventListener('click', () => this.pick(opt));
      }

      li.append(btn);
      list.append(li);
    }

    sheet.append(head, list);
    this.append(overlay, sheet);
  }

  async pick(option) {
    if (!option.input.checked) {
      const before = this.variantId;
      option.input.checked = true;
      option.input.dispatchEvent(new Event('change', { bubbles: true }));
      await this.waitForVariant(before);
    }
    this.chosen = true;
    this.close();
    this.resume();
  }

  /**
   * Доводимо до кінця дію, яку перервали воротами.
   *
   * Клік по розміру — це користувацький жест, і transient activation живе
   * кілька секунд, тож повторний клік по Shop Pay після очікування варіанта
   * ще вважається "за наміром користувача" і не блокується браузером.
   */
  resume() {
    const pending = this.pending;
    this.pending = null;

    if (pending && pending.type === 'payment') {
      // Dawn міг перерендерити секцію — беремо свіжий елемент, не збережений
      const container = document.querySelector('.shopify-payment-button');
      if (!container) return;
      const target =
        pending.which === 'more'
          ? container.querySelector('.shopify-payment-button__more-options')
          : container.querySelector('shop-pay-wallet-button');
      (target || container).click();
      return;
    }

    const form = this.form;
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.submit();
  }

  get variantId() {
    const input = this.form && this.form.querySelector('.product-variant-id');
    return input ? input.value : null;
  }

  /** Dawn перерендерює секцію асинхронно — чекаємо на новий id варіанта. */
  waitForVariant(before) {
    return new Promise((resolve) => {
      const started = Date.now();
      const tick = () => {
        if (this.variantId !== before || Date.now() - started > 2000) resolve();
        else setTimeout(tick, 50);
      };
      tick();
    });
  }
}

if (!customElements.get('size-gate')) {
  customElements.define('size-gate', SizeGate);
}
